/**
 * Java Validation Executor
 *
 * Handles the 'pipeline-java-validation' node execution.
 * Calls the ModernizeIT API Layer /java-validation/start endpoint.
 *
 * API Flow:
 * 1. POST /java-validation/start → returns job_id
 * 2. GET /java-validation/status/{job_id} → check status
 *
 * This node runs mechanical repair + structural validation on generated Java
 * before it reaches Java Packaging / Maven compile. Produces downloadable
 * report artifacts regardless of pass/fail.
 *
 * CRITICAL: On failure, attaches nodeResult to the thrown error so the UI
 * can still show "Download Artifacts" and "Open Analysis" buttons.
 */

import { pollJobUntilComplete } from '../../utils/jobPoller.js'
import { getAuthHeaders, handleApiError } from './authHelper.js'

/**
 * Execute the java-validation node
 *
 * @param {Object} config - Node configuration
 * @param {Object} context - Workflow execution context
 * @param {Function} onProgress - Progress callback (progress: 0-100, message: string)
 * @param {AbortSignal} signal - Abort signal for cancellation
 * @returns {Promise<Object>} - Execution result with job info and validation details
 */
export async function execute(config, context, onProgress = () => {}, signal = null) {
  const apiBaseUrl = context.api_base_url

  onProgress(0, 'Preparing Java validation...')

  if (signal?.aborted) {
    throw new Error('Execution cancelled')
  }

  // 1. Build request body
  const requestBody = {
    scout_account_id: context.scout_account_id,
    application_name: context.application_name,
    user_id: context.user_id,
    options: {
      strict_compile: config.strict_compile || false,
    },
  }

  onProgress(5, 'Submitting Java validation job...')

  // 2. Submit the job
  let jobResponse
  try {
    const response = await fetch(`${apiBaseUrl}/java-validation/start`, {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(requestBody),
      signal,
    })

    if (!response.ok) {
      await handleApiError(response)
    }

    jobResponse = await response.json()

    if (jobResponse.success === false) {
      throw new Error(jobResponse.error || 'Java validation failed')
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Execution cancelled')
    }
    if (error.message.includes('Failed to fetch')) {
      throw new Error(`Cannot connect to API at ${apiBaseUrl}. Is the server running?`)
    }
    throw error
  }

  const jobId = jobResponse.job_id
  if (!jobId) {
    throw new Error('API did not return a job_id')
  }

  onProgress(10, `Job started: ${jobId}`)

  // 3. Poll for completion
  if (jobResponse.status === 'completed' || jobResponse.status === 'SUCCEEDED') {
    onProgress(50, 'Java validation complete')
  } else {
    onProgress(15, 'Running Java repair + validation...')

    try {
      const completedJob = await pollJobUntilComplete(jobId, apiBaseUrl, {
        maxWaitSeconds: 900, // 15 min — download + repair + validate + upload can be slow
        pollInterval: 2000,
        onProgress: (progress, message) => {
          const mappedProgress = 15 + (progress * 0.75)
          onProgress(mappedProgress, message)
        },
        signal,
      })

      Object.assign(jobResponse, completedJob)
    } catch (pollError) {
      // Job failed — fetch output_data for artifact access
      // This is the CRITICAL failure path: attach nodeResult to error
      // so UI can show Download Artifacts + Open Analysis for failed jobs
      try {
        const statusResp = await fetch(
          `${apiBaseUrl}/java-validation/status/${jobId}`,
          { headers: getAuthHeaders() }
        )
        if (statusResp.ok) {
          const failedData = await statusResp.json()
          const partialResult = formatResult(jobId, failedData)
          partialResult.failed = true

          const err = new Error(
            failedData.error || pollError.message || 'Java Validation failed'
          )
          err.nodeResult = partialResult
          throw err
        }
      } catch (fetchErr) {
        // If status fetch itself throws with nodeResult, propagate it
        if (fetchErr.nodeResult) throw fetchErr
      }
      // Fallback: re-throw original poll error
      throw pollError
    }
  }

  onProgress(100, 'Java validation complete')

  return formatResult(jobId, jobResponse)
}

/**
 * Format the result object for consistent structure
 */
function formatResult(jobId, data) {
  return {
    // Job tracking — key name used by JPkg to find upstream
    jobs: {
      java_validation_job_id: jobId,
    },

    // Status
    success: data.success !== false && data.status !== 'failed',
    status: data.status || 'completed',

    // Validation summary
    validation_passed: data.validation_passed ?? null,
    files_checked: data.files_checked ?? 0,
    errors: data.errors ?? 0,
    warnings: data.warnings ?? 0,

    // Repair summary
    repair_changes: data.repair_changes ?? 0,

    // Error info
    error: data.error || null,
  }
}

export default {
  execute,
}
